import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { PlusCircle, Trash2, X, TrendingUp, Users, Calendar, DollarSign, BarChart3, Info } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { startOfMonth } from 'date-fns';

interface CashMovement {
  id: string;
  amount: number;
  kind: 'initial' | 'new_sale' | 'renewed_sale' | 'entry' | 'expense' | 'withdrawal';
  description?: string;
  created_at: string;
  unit_id: string;
  contract_id?: string;
  user_id?: string;
}

interface PaymentRecord {
  id: string;
  paid_amount: number | null;
  payment_date_time: string | null;
  unit_id: string;
  contract: {
    id: string;
    ruta: number;
  };
}

interface Cashbox {
  id: string;
  ruta: number;
  unit_id: string;
  opened_at: string;
  closed_at?: string;
  opened_by_user_id: string;
  closed_by_user_id?: string;
  initial_cash_amount: number;
  status: 'open' | 'closed';
  mobile_start: boolean;
}

interface CashMetrics {
  // Caixa Atual
  initialCash: number;
  newSales: number;
  renewedSales: number;
  totalSales: number;
  collections: number;
  income: number;
  expenses: number;
  withdrawals: number;
  availableCash: number;
  
  // Desempenho - Carteira
  portfolioFinal: number;
  portfolioVariation: number;
  portfolioInitial: number;
  portfolioCollected: number;
  
  // Desempenho - Recebimento
  intendedCollection: number;
  clientsToCollect: number;
  actualCollection: number;
  additionalCollection: number;
  compliance: number;
  unitCollectionPercentage: number;
  extraCollection: number;
  unscheduledClients: number;
  
  // Informações - Créditos
  creditsToCollect: number;
  creditsToCollectAmount: number;
  unscheduledCredits: number;
  unscheduledCreditsAmount: number;
  cancelledCredits: number;
  newCredits: number;
  activeCredits: number;
  
  // Informações - Movimento de Créditos
  paidCredits: number;
  unpaidCredits: number;
  synchronizedCredits: number;
  clientEfficiency: number;
}

export default function Cash() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [selectedRuta, setSelectedRuta] = useState<number>(1);
  const [cashMovements, setCashMovements] = useState<CashMovement[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [currentCashbox, setCurrentCashbox] = useState<Cashbox | null>(null);
  const [cashMetrics, setCashMetrics] = useState<CashMetrics>({
    initialCash: 0,
    newSales: 0,
    renewedSales: 0,
    totalSales: 0,
    collections: 0,
    income: 0,
    expenses: 0,
    withdrawals: 0,
    availableCash: 0,
    portfolioFinal: 0,
    portfolioVariation: 0,
    portfolioInitial: 0,
    portfolioCollected: 0,
    intendedCollection: 0,
    clientsToCollect: 0,
    actualCollection: 0,
    additionalCollection: 0,
    compliance: 0,
    unitCollectionPercentage: 0,
    extraCollection: 0,
    unscheduledClients: 0,
    creditsToCollect: 0,
    creditsToCollectAmount: 0,
    unscheduledCredits: 0,
    unscheduledCreditsAmount: 0,
    cancelledCredits: 0,
    newCredits: 0,
    activeCredits: 0,
    paidCredits: 0,
    unpaidCredits: 0,
    synchronizedCredits: 0,
    clientEfficiency: 0
  });
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    date: new Date()
  });
  const [newWithdrawal, setNewWithdrawal] = useState({
    description: '',
    amount: '',
    date: new Date()
  });

  useEffect(() => {
    fetchCashData(); 
  }, [selectedRuta, user]);

  const fetchCashData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);

      // Try to get current cashbox for the ruta (fallback if table doesn't exist)
      let currentCashbox: Cashbox | null = null;
      let periodStart = startOfMonth(new Date());
      let periodEnd = new Date();

      try {
        const cashboxResponse = await supabase
          .from('cashboxes')
          .select('*')
          .eq('unit_id', user.unit_id)
          .eq('ruta', selectedRuta)
          .order('opened_at', { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(response => response)
          .catch(error => ({ data: null, error }));

        const { data: cashboxData, error: cashboxError } = cashboxResponse;

        if (cashboxError) {
          if (cashboxError.code === '42P01') {
            console.warn('Cashboxes table does not exist, using fallback period');
          } else {
            console.error('Error fetching cashbox:', cashboxError);
          }
        }

        if (cashboxData) {
          currentCashbox = cashboxData;
          periodStart = currentCashbox.opened_at ? new Date(currentCashbox.opened_at) : startOfMonth(new Date());
          periodEnd = currentCashbox.closed_at ? new Date(currentCashbox.closed_at) : new Date();
        }
      } catch (error: any) {
        console.warn('Error accessing cashboxes table, using fallback period:', error.message);
      }

      // Try to fetch cash movements (fallback if table doesn't exist)
      let movementsData: CashMovement[] = [];
      try {
        const movementsResponse = await supabase
          .from('cash_movements')
          .select('*')
          .eq('unit_id', user.unit_id)
          .gte('created_at', periodStart.toISOString())
          .lte('created_at', periodEnd.toISOString())
          .then(response => response)
          .catch(error => ({ data: null, error }));

        const { data, error: movementsError } = movementsResponse;

        if (movementsError) {
          if (movementsError.code === '42P01') {
            console.warn('Cash movements table does not exist, using empty data');
          } else {
            console.error('Error fetching cash movements:', movementsError);
          }
        }

        movementsData = data || [];
      } catch (error: any) {
        console.warn('Error accessing cash movements table, using empty data:', error.message);
      }

      // Try to fetch payments (fallback if table doesn't exist)
      let installmentsData: PaymentRecord[] = [];
      let totalPaidAmount = 0;
      let totalLoanAmount = 0;
      try {
        const { data, error: paymentsError } = await supabase
          .from('installments')
          .select(`
            id,
            paid_amount,
            payment_date_time,
            unit_id,
            contract:contracts!inner(id, ruta, loan_amount)
          `)
          .eq('unit_id', user.unit_id)
          .not('paid_amount', 'is', null)
          .not('payment_date_time', 'is', null);

        if (paymentsError) {
          if (paymentsError.code === '42P01') {
            console.warn('Installments table does not exist, using empty data');
          } else {
            console.error('Error fetching installments:', paymentsError);
          }
        }

        installmentsData = data || [];
        
        // Calculate totals for the selected ruta
        const rutaInstallments = installmentsData.filter(p => 
          p.contract && p.contract.ruta === selectedRuta
        );
        
        totalPaidAmount = rutaInstallments.reduce((sum, p) => sum + (p.paid_amount || 0), 0);
        
        // Get unique contracts for the ruta to calculate total loan amount
        const uniqueContracts = new Map();
        rutaInstallments.forEach(p => {
          if (p.contract) {
            uniqueContracts.set(p.contract.id, p.contract.loan_amount);
          }
        });
        
        totalLoanAmount = Array.from(uniqueContracts.values()).reduce((sum: number, amount: number) => sum + amount, 0);
      } catch (error: any) {
        console.warn('Error accessing installments table, using empty data:', error.message);
      }

      // Calculate portfolioFinal using the specific query
      let portfolioFinal = 0;
      try {
        const { data: portfolioData, error: portfolioError } = await supabase
          .from('contracts')
          .select(`
            installments!inner(amount, paid_amount)
          `)
          .eq('approval_status', 'approved')
          .eq('ruta', selectedRuta)
          .eq('unit_id', user.unit_id)
          .eq('installments.status', 'pending')
          .like('client_name', '%[A-Za-z]%'); // Simplified regex check for letters

        if (portfolioError) {
          console.warn('Error fetching portfolio data:', portfolioError);
        } else if (portfolioData) {
          portfolioFinal = portfolioData.reduce((sum, contract) => {
            return sum + contract.installments.reduce((installmentSum: number, installment: any) => {
              const remainingAmount = (installment.amount || 0) - (installment.paid_amount || 0);
              return installmentSum + Math.max(remainingAmount, 0);
            }, 0);
          }, 0);
        }
      } catch (error: any) {
        console.warn('Error calculating portfolio final:', error.message);
      }

      // Calculate metrics

      // Caixa Atual calculations
      const initialCash = currentCashbox?.initial_cash_amount || 0;

      // Calculate cash movements by kind
      const newSales = (movementsData || [])
        .filter(m => m.kind === 'new_sale')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0); // Ensure positive for display, will be negative in formula

      const renewedSales = (movementsData || [])
        .filter(m => m.kind === 'renewed_sale')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0); // Ensure positive for display

      const totalSales = newSales + renewedSales;

      const collections = totalPaidAmount;

      const income = (movementsData || [])
        .filter(m => m.kind === 'entry')
        .reduce((sum, m) => sum + m.amount, 0);

      const expenses = (movementsData || [])
        .filter(m => m.kind === 'expense')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0); // Ensure positive for display

      const withdrawals = (movementsData || [])
        .filter(m => m.kind === 'withdrawal')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0); // Ensure positive for display

      // Calculate available cash using the formula:
      // Caixa Atual = Total de installments.paid_amount - Total de contracts.loan_amount
      const availableCash = totalPaidAmount - totalLoanAmount;

      // For now, set placeholder values for Desempenho and Informações sections
      // These will be implemented in the next steps
      setCashMetrics({
        initialCash,
        newSales,
        renewedSales,
        totalSales,
        collections,
        income,
        expenses,
        withdrawals,
        availableCash,
        portfolioFinal,
        portfolioVariation: 0, // TODO: Implement in next step
        portfolioInitial: 0, // TODO: Implement in next step
        portfolioCollected: collections,
        intendedCollection: 0, // TODO: Implement in next step
        clientsToCollect: 0, // TODO: Implement in next step
        actualCollection: collections,
        additionalCollection: 0, // TODO: Implement in next step
        compliance: 0, // TODO: Implement in next step
        unitCollectionPercentage: 0, // TODO: Implement in next step
        extraCollection: 0, // TODO: Implement in next step
        unscheduledClients: 0, // TODO: Implement in next step
        creditsToCollect: 0, // TODO: Implement in next step
        creditsToCollectAmount: 0, // TODO: Implement in next step
        unscheduledCredits: 0, // TODO: Implement in next step
        unscheduledCreditsAmount: 0, // TODO: Implement in next step
        cancelledCredits: 0, // TODO: Implement in next step
        newCredits: 0, // TODO: Implement in next step
        activeCredits: 0, // TODO: Implement in next step
        paidCredits: 0, // TODO: Implement in next step
        unpaidCredits: 0, // TODO: Implement in next step
        synchronizedCredits: 0, // TODO: Implement in next step
        clientEfficiency: 0 // TODO: Implement in next step
      });

      setCashMovements(movementsData);
      setPayments(installmentsData);
      setCurrentCashbox(currentCashbox);

    } catch (err) {
      console.error('Error fetching cash data:', err);
      setError('Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'expense',
          amount: -Math.abs(parseFloat(newExpense.amount)), // Ensure negative value
          description: newExpense.description,
          unit_id: user?.unit_id,
          user_id: user?.id,
          created_at: newExpense.date.toISOString()
        });

      if (error) throw error;

      setShowExpenseModal(false);
      setNewExpense({
        description: '',
        amount: '',
        date: new Date()
      });
      await fetchCashData();
    } catch (err) {
      console.error('Error adding expense:', err);
      setError('Erro ao adicionar despesa');
    }
  };

  const handleAddWithdrawal = async () => {
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'withdrawal',
          amount: -Math.abs(parseFloat(newWithdrawal.amount)),
          description: newWithdrawal.description,
          unit_id: user?.unit_id,
          user_id: user?.id,
          created_at: newWithdrawal.date.toISOString()
        });

      if (error) throw error;

      setShowWithdrawalModal(false);
      setNewWithdrawal({
        description: '',
        amount: '',
        date: new Date()
      });
      await fetchCashData();
    } catch (err) {
      console.error('Error adding withdrawal:', err);
      setError('Erro ao adicionar retirada');
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta despesa?')) return;

    try {
      const { error } = await supabase
        .from('cash_movements')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      await fetchCashData();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Erro ao excluir despesa');
    }
  };

  const handleDeleteWithdrawal = async (withdrawalId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta retirada?')) return;

    try {
      const { error } = await supabase
        .from('cash_movements')
        .delete()
        .eq('id', withdrawalId);

      if (error) throw error;

      await fetchCashData();
    } catch (err) {
      console.error('Error deleting withdrawal:', err);
      setError('Erro ao excluir retirada');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Caixa</h1>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Selecionar Ruta:</label>
            <select
              value={selectedRuta}
              onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            >
              <option value={1}>Ruta 1</option>
              <option value={2}>Ruta 2</option>
              <option value={3}>Ruta 3</option>
              <option value={4}>Ruta 4</option>
              <option value={5}>Ruta 5</option>
              <option value={6}>Ruta 6</option>
              <option value={7}>Ruta 7</option>
              <option value={8}>Ruta 8</option>
            </select>
          </div>
        </div>

        {/* Cashbox Status Info */}
        {currentCashbox && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-800">
                  Caixa {currentCashbox.status === 'open' ? 'Aberto' : 'Fechado'} - Ruta {currentCashbox.ruta}
                </h3>
                <p className="text-xs text-blue-600">
                  Aberto em: {new Date(currentCashbox.opened_at).toLocaleDateString('pt-BR')} às {new Date(currentCashbox.opened_at).toLocaleTimeString('pt-BR')}
                  {currentCashbox.closed_at && (
                    <> • Fechado em: {new Date(currentCashbox.closed_at).toLocaleDateString('pt-BR')} às {new Date(currentCashbox.closed_at).toLocaleTimeString('pt-BR')}</>
                  )}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-blue-800">
                  Caixa Inicial: {formatCurrency(currentCashbox.initial_cash_amount)}
                </p>
                {currentCashbox.mobile_start && (
                  <p className="text-xs text-blue-600">Iniciado pelo celular</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          
          {/* Left Column - Caixa Atual (Purple) */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Caixa Atual</h2>
            
            {/* Main Balance Display */}
            <div className="mb-8">
              <div className="text-center">
                <p className="text-sm opacity-80 mb-2">BRL R$</p>
                <p className="text-4xl font-bold mb-2">
                  {formatCurrency(cashMetrics.availableCash).replace('R$', '').trim()}
                </p>
                <p className="text-xs opacity-70">
                  {new Date().toLocaleDateString('pt-BR')} {new Date().toLocaleTimeString('pt-BR')}
                </p>
              </div>
            </div>

            {/* Cash Flow Breakdown */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm">Caixa Inicial</span>
                <span className="text-green-300 font-medium">
                  {formatCurrency(cashMetrics.initialCash)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Novas Vendas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.newSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Vendas Renovadas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.renewedSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Total de Vendas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.totalSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Recebimento</span>
                <span className="text-green-300 font-medium">
                  +{formatCurrency(cashMetrics.collections)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Entradas</span>
                <span className="text-green-300 font-medium">
                  +{formatCurrency(cashMetrics.income)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Gastos</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.expenses)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Retiradas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.withdrawals)}
                </span>
              </div>
            </div>

            {/* Expense and Withdrawal Management Buttons */}
            <div className="mt-8 pt-6 border-t border-purple-400">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowExpenseModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span className="text-sm">Gastos</span>
                </button>
                <button
                  onClick={() => setShowWithdrawalModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span className="text-sm">Retiradas</span>
                </button>
              </div>
            </div>
          </div>

          {/* Middle Column - Desempenho (Yellow/Orange) */}
          <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Desempenho</h2>
            
            {/* Portfolio Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Carteira</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Final</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.portfolioFinal)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Variação</span>
                  <span className="text-green-200 font-medium">
                    {formatPercentage(cashMetrics.portfolioVariation)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Inicial</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.portfolioInitial)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Carteira Recebida</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.portfolioCollected)}
                  </span>
                </div>
              </div>
            </div>

            {/* Collections Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Recebimento</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Pretendido</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.intendedCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Clientes a receber</span>
                  <span className="text-white font-medium">
                    {cashMetrics.clientsToCollect}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.actualCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento Adicional</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.additionalCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Cumprimento</span>
                  <span className="text-green-200 font-medium">
                    {formatPercentage(cashMetrics.compliance)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">% Recebimento da Unidade</span>
                  <span className="text-white font-medium">
                    {formatPercentage(cashMetrics.unitCollectionPercentage)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento Extra</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.extraCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Clientes Não Programados</span>
                  <span className="text-white font-medium">
                    {cashMetrics.unscheduledClients}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Informações (Blue) */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Informações</h2>
            
            {/* Cash Box Info */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Caixa da Ruta {selectedRuta}</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg">
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">Trabalhador</p>
                </div>
                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg">
                  <Calendar className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">Calendário</p>
                </div>
                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg">
                  <BarChart3 className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">Estatísticas</p>
                </div>
                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg">
                  <Info className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-xs">Informações</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Trabalhador:</span>
                  <span>{user?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Início pelo Celular:</span>
                  <span>{currentCashbox?.mobile_start ? 'Sim' : 'Não'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Data de Abertura:</span>
                  <span>
                    {currentCashbox ? 
                      new Date(currentCashbox.opened_at).toLocaleDateString('pt-BR') : 
                      new Date().toLocaleDateString('pt-BR')
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Data de Fechamento:</span>
                  <span>
                    {currentCashbox?.closed_at ? 
                      new Date(currentCashbox.closed_at).toLocaleDateString('pt-BR') : 
                      'Em aberto'
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Credits Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 border-b border-blue-300 pb-2">Créditos</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">A Receber</span>
                  <div className="text-right">
                    <span className="text-white font-medium">{cashMetrics.creditsToCollect}</span>
                    <span className="text-blue-200 text-xs block">
                      {formatCurrency(cashMetrics.creditsToCollectAmount)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Não Programados</span>
                  <div className="text-right">
                    <span className="text-green-200 font-medium">{cashMetrics.unscheduledCredits}</span>
                    <span className="text-blue-200 text-xs block">
                      {formatCurrency(cashMetrics.unscheduledCreditsAmount)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Cancelados</span>
                  <span className="text-red-200 font-medium">{cashMetrics.cancelledCredits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Novos</span>
                  <span className="text-green-200 font-medium">{cashMetrics.newCredits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Ativos</span>
                  <span className="text-white font-medium">{cashMetrics.activeCredits}</span>
                </div>
              </div>
            </div>

            {/* Credit Movement */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b border-blue-300 pb-2">Movimento de Créditos</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Pagos</span>
                  <div className="text-right">
                    <span className="text-white font-medium">{cashMetrics.paidCredits}</span>
                    <span className="text-blue-200 text-xs block">Pago</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Não Pago</span>
                  <span className="text-white font-medium">{cashMetrics.unpaidCredits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Sincronizados</span>
                  <span className="text-green-200 font-medium">{cashMetrics.synchronizedCredits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Eficiência de Clientes</span>
                  <span className="text-white font-medium">
                    {formatPercentage(cashMetrics.clientEfficiency)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expense Management Modal */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  Gerenciar Despesas - Ruta {selectedRuta}
                </h2>
                <button
                  onClick={() => {
                    setShowExpenseModal(false);
                    setNewExpense({
                      description: '',
                      amount: '',
                      date: new Date()
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Add Expense and Withdrawal Form */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Adicionar Novo Gasto</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={newExpense.description}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    <input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data
                    </label>
                    <DatePicker
                      selected={newExpense.date}
                      onChange={(date) => setNewExpense(prev => ({ ...prev, date: date || new Date() }))}
                      dateFormat="dd/MM/yyyy"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    onClick={handleAddExpense}
                    disabled={!newExpense.description || !newExpense.amount}
                    className={`w-full py-2 px-4 rounded-md text-white transition-colors ${
                      !newExpense.description || !newExpense.amount
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    Adicionar Gasto
                  </button>
                </div>
              </div>

              {/* Add Withdrawal Form */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Adicionar Nova Retirada</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição
                    </label>
                    <input
                      type="text"
                      value={newWithdrawal.description}
                      onChange={(e) => setNewWithdrawal(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    <input
                      type="number"
                      value={newWithdrawal.amount}
                      onChange={(e) => setNewWithdrawal(prev => ({ ...prev, amount: e.target.value }))}
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data
                    </label>
                    <DatePicker
                      selected={newWithdrawal.date}
                      onChange={(date) => setNewWithdrawal(prev => ({ ...prev, date: date || new Date() }))}
                      dateFormat="dd/MM/yyyy"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    onClick={handleAddWithdrawal}
                    disabled={!newWithdrawal.description || !newWithdrawal.amount}
                    className={`w-full py-2 px-4 rounded-md text-white transition-colors ${
                      !newWithdrawal.description || !newWithdrawal.amount
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    Adicionar Retirada
                  </button>
                </div>
              </div>

              {/* Expenses List */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Gastos Registrados</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descrição
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cashMovements
                        .filter(movement => movement.kind === 'expense')
                        .map((expense) => (
                        <tr key={expense.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(expense.created_at).toLocaleDateString('pt-BR')}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {expense.description || 'Gasto'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-red-600 font-medium">
                              {formatCurrency(Math.abs(expense.amount))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {cashMovements.filter(movement => movement.kind === 'expense').length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                            Nenhum gasto registrado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Withdrawals List */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Retiradas Registradas</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descrição
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cashMovements
                        .filter(movement => movement.kind === 'withdrawal')
                        .map((withdrawal) => (
                        <tr key={withdrawal.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(withdrawal.created_at).toLocaleDateString('pt-BR')}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {withdrawal.description || 'Retirada'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-red-600 font-medium">
                              {formatCurrency(Math.abs(withdrawal.amount))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteWithdrawal(withdrawal.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {cashMovements.filter(movement => movement.kind === 'withdrawal').length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                            Nenhuma retirada registrada
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}